import { useState } from 'react';
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  DataGridProps,
} from '@fluentui/react-components';
import { Stack } from '@fluentui/react';
import { EditRegular, DeleteRegular } from '@fluentui/react-icons';
import Button from '../../../components/Button';
import { JobRole } from '../../../api';

type JobRoleKey = keyof Omit<JobRole, 'PartitionKey' | 'RowKey'>;

type Props = {
  jobRoles: JobRole[];
  onEdit: (jobRole: JobRole) => void;
  onDelete: (jobRole: JobRole) => void;
};

const titles: Record<JobRoleKey, string> = {
  Job: 'Job Title',
  Company: '',
  Consultant: '',
  Discipline: '',
  Industry1: 'Industry',
  Location: '',
  Sector: '',
  Seniority: '',
  DateAdded: 'Date Added',
};

const columns: TableColumnDefinition<JobRole>[] = Object.entries(titles).map(([k, title]) => {
  const key = k as JobRoleKey;
  return createTableColumn<JobRole>({
    columnId: key,
    compare: (a, b) => {
      return (a[key] ?? '').localeCompare(b[key] ?? '');
    },
    renderHeaderCell: () => title || key,
    renderCell: item => {
      if (key === 'DateAdded' && item[key]) {
        return new Date(item[key]).toLocaleDateString();
      }
      const str = item[key] == null ? '' : key == 'Location' ? item[key].replace(/, /g, ' ') : item[key];
      return str.trim();
    },
  });
});

const JobRolesTable = ({ jobRoles, onEdit, onDelete }: Props) => {
  const [sortState, setSortState] = useState<Parameters<NonNullable<DataGridProps['onSortChange']>>[1]>({
    sortColumn: 'Job',
    sortDirection: 'ascending',
  });

  const onSortChange: DataGridProps['onSortChange'] = (e, nextSortState) => {
    setSortState(nextSortState);
  };

  // add the edit/delete actions
  if (!columns.find(c => c.columnId == 'actions'))
    columns.push(
      createTableColumn<JobRole>({
        columnId: 'actions',
        renderHeaderCell: () => 'Actions',
        renderCell: item => (
          <Stack horizontal style={{ gap: '8px', marginTop: '8px' }}>
            <Button icon={EditRegular} onClick={() => onEdit(item)} />
            <Button icon={DeleteRegular} onClick={() => onDelete(item)} />
          </Stack>
        ),
      }),
    );

  return (
    <DataGrid
      items={jobRoles}
      columns={columns}
      sortable
      sortState={sortState}
      onSortChange={onSortChange}
      style={{ minWidth: '500px' }}
    >
      <DataGridHeader>
        <DataGridRow style={{ padding: '8px 0', borderBottom: '1px solid #ccc', fontWeight: 'bold' }}>
          {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
        </DataGridRow>
      </DataGridHeader>
      <DataGridBody<JobRole>>
        {({ item, rowId }) => (
          <DataGridRow<JobRole> key={rowId} style={{ padding: '8px 0', borderBottom: '1px solid #ccc' }}>
            {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
          </DataGridRow>
        )}
      </DataGridBody>
    </DataGrid>
  );
};

export default JobRolesTable;
